import type { AssignmentCompletion, Job, JobPriority, JobStatus, PaymentStatus } from '@workflow/contracts'
import { JOB_TRANSITIONS } from '@workflow/contracts'
import type { StatusHue, StatusTone } from '@workflow/ui'

/**
 * How each status is shown — the same silhouettes the account and client
 * statuses use, so one scan reads all of them: live, waiting, or at rest —
 * and a colour each, so the ledger reads at a glance: blue is open work,
 * amber is paused, green is finished, violet is removed by the client (still
 * paid), red is cancelled. A draft stays grey.
 */
export const JOB_STATUS: Record<JobStatus, { label: string; tone: StatusTone; hue?: StatusHue }> = {
  draft: { label: 'Draft', tone: 'idle' },
  published: { label: 'Published', tone: 'waiting', hue: 'blue' },
  in_progress: { label: 'In Progress', tone: 'done', hue: 'blue' },
  on_hold: { label: 'Paused', tone: 'waiting', hue: 'amber' },
  completed: { label: 'Completed', tone: 'done', hue: 'green' },
  // The client pulled the work after it began; the hours worked still pay.
  removed: { label: 'Removed', tone: 'done', hue: 'violet' },
  cancelled: { label: 'Cancelled', tone: 'idle', hue: 'red' },
}

export const ASSIGNMENT_STATE: Record<AssignmentCompletion, { label: string; tone: StatusTone }> = {
  assigned: { label: 'Assigned', tone: 'waiting' },
  in_progress: { label: 'In Progress', tone: 'done' },
  completed: { label: 'Completed', tone: 'done' },
  dropped: { label: 'Dropped', tone: 'idle' },
  removed: { label: 'Removed', tone: 'idle' },
}

/**
 * A finished part that came in with fewer hours than it was assigned: the
 * work is in, the hours are not. Amber, like anything waiting on a decision.
 */
export const HANDED_IN_SHORT = { label: 'Handed In Short', tone: 'done', hue: 'amber' } as const satisfies { label: string; tone: StatusTone; hue: StatusHue }

/** Whether a finished part holds fewer hours than its allocation. */
export const isShort = (part: { completion: AssignmentCompletion; estimatedHours: number | null; actualHours: number | null }): boolean =>
  part.completion === 'completed' && part.estimatedHours !== null && part.estimatedHours > 0
    && (part.actualHours ?? part.estimatedHours) < part.estimatedHours

/** The state one part shows: its completion, or Handed In Short when it finished below its hours. */
export const partState = (part: { completion: AssignmentCompletion; estimatedHours: number | null; actualHours: number | null }): { label: string; tone: StatusTone; hue?: StatusHue } =>
  isShort(part) ? HANDED_IN_SHORT : ASSIGNMENT_STATE[part.completion]

export const PAYMENT_STATUS: Record<PaymentStatus, { label: string; tone: StatusTone }> = {
  unpaid: { label: 'Unpaid', tone: 'idle' },
  pending: { label: 'Payment pending', tone: 'waiting' },
  paid: { label: 'Paid', tone: 'done' },
}

export const PRIORITY_LABEL: Record<JobPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

/**
 * The verb for moving a job *to* a status. `in_progress` reads differently depending on where the job comes
 * from — starting, or resuming — and the verbs component picks the word.
 */
export const TARGET_VERB: Partial<Record<JobStatus, string>> = {
  draft: 'Return to draft',
  published: 'Publish',
  in_progress: 'Start Work',
  on_hold: 'Pause',
  completed: 'Mark Completed',
  removed: 'Mark Removed',
  cancelled: 'Cancel',
}

/**
 * The moves a job can make from where it stands, worded as decisions — the
 * transition map the API enforces, less the steps it would refuse for this
 * job (publishing is an administrator's; started work never returns to
 * draft or to Published). The header's menu and the row's circle both ask here.
 */
export function statusMoves(job: Job, isAdmin: boolean): { target: JobStatus; label: string }[] {
  if (job.archivedAt !== null) return []
  return JOB_TRANSITIONS[job.status]
    .filter((target) =>
      (target !== 'published' || isAdmin) && !(target === 'draft' && job.actualStart !== null) &&
      !(target === 'published' && job.status === 'on_hold' && job.actualStart !== null))
    .map((target) => ({
      target,
      label: target === 'in_progress'
        ? (job.status === 'on_hold' ? 'Resume' : job.actualStart !== null ? 'Reopen' : 'Start Work')
        : (TARGET_VERB[target] ?? target),
    }))
}

/** Money is an integer of minor units; this is where it becomes readable. */
export function formatMoney(minor: number | null): string {
  if (minor === null) return '—'
  return new Intl.NumberFormat('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(minor / 100)
}

/** Hours read as the studio says them: whole where they are, one decimal where not. */
/** A count of hours with no unit — for a figure that a word beside it already names. */
export function hoursFigure(value: number): string {
  const rounded = Math.round(value * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

export function formatHours(value: number | null): string {
  if (value === null) return '—'
  const rounded = Math.round(value * 10) / 10
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}h`
}
